import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import {
    ListingService,
    MyListing
} from '@/hotdesk-ui/services/listing.service';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import {
    geoAreaScope,
    geoCentreCircle
} from '@/hotdesk-ui/helpers/explore-location-params';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { InlineResponse2006, ReadOnlyListing } from 'hotdesk_typescript';
import { RootState } from '../store';
import { parseData } from '@/hotdesk-ui/helpers/utils';
import { getUserSubscriptionIds } from '@/hotdesk-ui/utils/subscriptionUtils';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';

export interface InitialStateListing {
    workspaces: ReadOnlyListing[];
    listings: MyListing[];
    activeButton: Carousel;
    totallisting: number;
    totalWorkspace: number;
    defaultLanguage: string;
    workspaceBtnLoading?: boolean;
    error?: string | null;
    workspacePage: number;
    listingPage: number;
    radius: number;
    listingBtnLoading?: boolean;
    filters: { [key: string]: string };
    isWorkspaceLoading: boolean;
    /**
     * A workspace fetch has settled at least once this session.
     *
     * `isWorkspaceLoading` starts false and only flips true once a fetch is
     * dispatched, so before that "not loading" and "finished, found nothing"
     * are indistinguishable — which is how the landing's expansion banner came
     * to render while geolocation was still resolving. Deliberately not
     * persisted: a previous session having fetched says nothing about this one.
     */
    workspacesFetched: boolean;
    isListingLoading: boolean;
    /**
     * requestId of the newest getWorkspaceData. On load the IP and GPS
     * location fetches overlap; an older answer arriving last must not
     * replace the newer cards (or end the newer request's loading state).
     * Not persisted.
     */
    latestWorkspaceRequestId?: string;
    // Ids the user's active subscription actually covers (promo-based), resolved
    // via a filtered query. Used to badge cards as "Applied" on any view — the
    // per-row `metaData.applicableSubscriptions` is only reliable on the filtered
    // explore query (its distinct=workspace representative is a covered listing).
    coveredWorkspaceIds: number[];
    coveredListingIds: number[];
}

export const initialState: InitialStateListing = {
    workspaces: [],
    listings: [],
    activeButton: Carousel.workspace,
    totalWorkspace: 0,
    totallisting: 0,
    defaultLanguage: 'en',
    workspaceBtnLoading: false,
    error: null,
    workspacePage: 1,
    listingPage: 1,
    radius: 15,
    listingBtnLoading: false,
    filters: {},
    isWorkspaceLoading: false,
    workspacesFetched: false,
    isListingLoading: false,
    coveredWorkspaceIds: [],
    coveredListingIds: []
};

const getApiFilters = (filters: Record<string, string>) => {
    // `currency` makes the listing API return 502 for every value, and price
    // filters are sent pre-converted to USD instead. `filters` is persisted, so
    // strip it here to also drop values stored by earlier builds.
    const withoutCurrency = { ...filters };
    delete withoutCurrency.currency;
    if (!withoutCurrency.userSubscriptionIdIn) {
        const { userSubscriptionIdIn: _, ...rest } = withoutCurrency;
        return rest;
    }
    return withoutCurrency;
};

export { getApiFilters };

export const getListingData = createAsyncThunk(
    'listing/getListingData',
    async (
        {
            page,
            limit = 20,
            lastSearch,
            country,
            city,
            filters: filtersOverride,
            searchLocation,
            keepPreviousResults
        }: {
            page: number;
            limit?: number;
            lastSearch?: GeoCodeResult;
            country?: string;
            city?: string;
            filters?: Record<string, string>;
            searchLocation?: { lat: number; lng: number; radius: number };
            /** Keep current list visible until the fetch completes (map zoom refresh). */
            keepPreviousResults?: boolean;
        },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage, radius, filters: storeFilters } = state.listing;
        const apiFilters = getApiFilters(filtersOverride ?? storeFilters);

        try {
            const apiConfig = await new ApiConfigration().getApiConfig(
                defaultLanguage
            );
            const service = new ListingService(apiConfig);
            let listingRes;
            if (!!country && !!city) {
                listingRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.listing,
                        limit,
                        (page - 1) * limit,
                        null,
                        apiFilters,
                        country,
                        city
                    )
                );
            } else if (!!country) {
                listingRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.listing,
                        limit,
                        (page - 1) * limit,
                        null,
                        apiFilters,
                        country
                    )
                );
            } else if (searchLocation) {
                listingRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.listing,
                        limit,
                        (page - 1) * limit,
                        searchLocation,
                        apiFilters
                    )
                );
            } else if (geoAreaScope(lastSearch)) {
                // Whole-country search (e.g. "Egypt"): a lat/lng radius around
                // the country centroid returns 0 — filter by country code instead.
                // A whole state ("California") adds its name within the country.
                const scope = geoAreaScope(lastSearch)!;
                listingRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.listing,
                        limit,
                        (page - 1) * limit,
                        geoCentreCircle(lastSearch, radius),
                        apiFilters,
                        undefined,
                        undefined,
                        undefined,
                        scope.countryCode,
                        scope.region
                    )
                );
            } else if (!!lastSearch?.lat && lastSearch?.log) {
                listingRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.listing,
                        limit,
                        (page - 1) * limit,
                        {
                            lat: lastSearch.lat as number,
                            lng: lastSearch.log as number,
                            radius
                        },
                        apiFilters
                    )
                );
            }
            return parseData(listingRes);
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);

export const getWorkspaceData = createAsyncThunk(
    'listing/getWorkspaceData',
    async (
        {
            page,
            limit = 20,
            lastSearch,
            country,
            city,
            filters: filtersOverride,
            searchLocation,
            keepPreviousResults
        }: {
            page: number;
            limit?: number;
            lastSearch?: GeoCodeResult;
            country?: string;
            city?: string;
            filters?: Record<string, string>;
            searchLocation?: { lat: number; lng: number; radius: number };
            /** Keep current list visible until the fetch completes (map zoom refresh). */
            keepPreviousResults?: boolean;
        },
        { getState }
    ) => {
        const state = getState() as RootState;
        const { defaultLanguage, radius, filters: storeFilters } = state.listing;
        const apiFilters = getApiFilters(filtersOverride ?? storeFilters);
        try {
            const apiConfig = await new ApiConfigration().getApiConfig(
                defaultLanguage
            );
            let workspaceRes;
            const service = new ListingService(apiConfig);
            if (!!country && !!city) {
                workspaceRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.workspace,
                        limit,
                        (page - 1) * limit,
                        null,
                        apiFilters,
                        country,
                        city
                    )
                );
            } else if (!!country) {
                workspaceRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.workspace,
                        limit,
                        (page - 1) * limit,
                        null,
                        apiFilters,
                        country
                    )
                );
            } else if (searchLocation) {
                workspaceRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.workspace,
                        limit,
                        (page - 1) * limit,
                        searchLocation,
                        apiFilters
                    )
                );
            } else if (geoAreaScope(lastSearch)) {
                // Whole-country search (e.g. "Egypt"): a lat/lng radius around
                // the country centroid returns 0 — filter by country code instead.
                // A whole state ("California") adds its name within the country.
                const scope = geoAreaScope(lastSearch)!;
                workspaceRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.workspace,
                        limit,
                        (page - 1) * limit,
                        geoCentreCircle(lastSearch, radius),
                        apiFilters,
                        undefined,
                        undefined,
                        undefined,
                        scope.countryCode,
                        scope.region
                    )
                );
            } else if (!!lastSearch?.lat && lastSearch?.log) {
                workspaceRes = await service.getallListings(
                    service.getListingsDataParams(
                        Carousel.workspace,
                        limit,
                        (page - 1) * limit,
                        {
                            lat: lastSearch.lat as number,
                            lng: lastSearch.log as number,
                            radius
                        },
                        apiFilters
                    )
                );
            }
            return parseData(workspaceRes);
        } catch (error) {
            console.error('Error getting results', error);
            throw error;
        }
    }
);

// Resolve the ids the user's active subscription actually covers (promo-based)
// near a location, by running the same subscription-filtered query the explore
// view uses. The result drives the "Applied" badge on any view — including the
// unfiltered landing, where per-row `applicableSubscriptions` is empty.
export const fetchSubscriptionCoverage = createAsyncThunk(
    'listing/fetchSubscriptionCoverage',
    async (
        { lastSearch }: { lastSearch?: GeoCodeResult },
        { getState }
    ): Promise<{ workspaceIds: number[]; listingIds: number[] }> => {
        const state = getState() as RootState;
        const { defaultLanguage, radius } = state.listing;
        const subscriptionIdIn = getUserSubscriptionIds(state.auth.user).join(
            ','
        );
        if (!subscriptionIdIn || !lastSearch?.lat || !lastSearch?.log) {
            return { workspaceIds: [], listingIds: [] };
        }

        const apiConfig = await new ApiConfigration().getApiConfig(
            defaultLanguage
        );
        const service = new ListingService(apiConfig);
        const loc = {
            lat: lastSearch.lat as number,
            lng: lastSearch.log as number,
            radius
        };
        const filters = { userSubscriptionIdIn: subscriptionIdIn };

        const [wsRes, listRes] = await Promise.all([
            service.getallListings(
                service.getListingsDataParams(
                    Carousel.workspace,
                    200,
                    0,
                    loc,
                    filters
                )
            ),
            service.getallListings(
                service.getListingsDataParams(
                    Carousel.listing,
                    200,
                    0,
                    loc,
                    filters
                )
            )
        ]);

        const workspaceIds = (wsRes?.results || [])
            .map((r) => (r as ReadOnlyListing).workspace?.id)
            .filter((id): id is number => id != null);
        const listingIds = (listRes?.results || [])
            .map((r) => (r as ReadOnlyListing).id)
            .filter((id): id is number => id != null);

        return { workspaceIds, listingIds };
    }
);

export const listingSlice = createSlice({
    name: 'listing',
    initialState,
    reducers: {
        setActiveButton(state, action: PayloadAction<Carousel>) {
            state.activeButton = action.payload;
        },
        setListingPage(state, action: PayloadAction<number>) {
            state.listingPage = action.payload;
        },
        setWorkspacePage(state, action: PayloadAction<number>) {
            state.workspacePage = action.payload;
        },
        setWorkspaces(state, action: PayloadAction<ReadOnlyListing[]>) {
            state.workspaces = action.payload;
        },
        setListings(state, action: PayloadAction<MyListing[]>) {
            state.listings = action.payload;
        },
        setTotallisting(state, action: PayloadAction<number>) {
            state.totallisting = action.payload;
        },
        setTotalWorkspace(state, action: PayloadAction<number>) {
            state.totalWorkspace = action.payload;
        },
        setFilters(state, action: PayloadAction<Record<string, string>>) {
            state.filters = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(getListingData.pending, (state, action) => {
                state.listingBtnLoading = true;
                state.isListingLoading = true;
                if (
                    state.listingPage === 1 &&
                    !action.meta.arg.keepPreviousResults
                ) {
                    state.listings = [];
                }
                state.error = null;
            })
            .addCase(
                getListingData.fulfilled,
                (
                    state,
                    action: PayloadAction<InlineResponse2006 | undefined>
                ) => {
                    const items = (action.payload as InlineResponse2006)
                        .results;
                    const count = (action.payload as InlineResponse2006).count;

                    state.listingBtnLoading = false;
                    state.isListingLoading = false;

                    if (state.listingPage === 1) {
                        state.listings = items;
                    } else {
                        if (items.length > 0) {
                            state.listings.push(...items);
                        }
                    }
                    state.totallisting = count;
                }
            )
            .addCase(getListingData.rejected, (state, action) => {
                state.listingBtnLoading = false;
                // Without this the grid stays on skeletons forever, and the
                // stale count keeps "Show more" visible over an empty list.
                state.isListingLoading = false;
                if (state.listings.length === 0) {
                    state.totallisting = 0;
                }
                state.error = action.payload as string;
            })
            .addCase(getWorkspaceData.pending, (state, action) => {
                state.latestWorkspaceRequestId = action.meta.requestId;
                state.workspaceBtnLoading = true;
                state.isWorkspaceLoading = true;
                if (
                    state.workspacePage === 1 &&
                    !action.meta.arg.keepPreviousResults
                ) {
                    state.workspaces = [];
                }
                state.error = null;
            })
            .addCase(
                getWorkspaceData.fulfilled,
                (state, action) => {
                    if (action.meta.requestId !== state.latestWorkspaceRequestId) {
                        return;
                    }
                    const items = (action.payload as InlineResponse2006)
                        .results;
                    const count = (action.payload as InlineResponse2006).count;

                    state.workspaceBtnLoading = false;
                    state.isWorkspaceLoading = false;
                    state.workspacesFetched = true;

                    if (state.workspacePage === 1) {
                        state.workspaces = items;
                    } else {
                        if (items.length > 0) {
                            state.workspaces.push(...items);
                        }
                    }
                    state.totalWorkspace = count;
                }
            )
            .addCase(getWorkspaceData.rejected, (state, action) => {
                if (action.meta.requestId !== state.latestWorkspaceRequestId) {
                    return;
                }
                state.workspaceBtnLoading = false;
                // See getListingData.rejected.
                state.isWorkspaceLoading = false;
                // A failed answer is still an answer: the caller has asked.
                state.workspacesFetched = true;
                if (state.workspaces.length === 0) {
                    state.totalWorkspace = 0;
                }
                state.error = action.payload as string;
            })
            .addCase(
                fetchSubscriptionCoverage.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        workspaceIds: number[];
                        listingIds: number[];
                    }>
                ) => {
                    state.coveredWorkspaceIds = action.payload.workspaceIds;
                    state.coveredListingIds = action.payload.listingIds;
                }
            );
    }
});

export const {
    setActiveButton,
    setListingPage,
    setWorkspacePage,
    setListings,
    setWorkspaces,
    setTotalWorkspace,
    setTotallisting,
    setFilters
} = listingSlice.actions;

const persistConfig = {
    key: 'listing',
    storage,
    blacklist: [
        'workspacePage',
        'listingPage',
        'coveredWorkspaceIds',
        'coveredListingIds',
        'workspacesFetched',
        'latestWorkspaceRequestId'
    ]
};

export default persistReducer(persistConfig, listingSlice.reducer);
